import {
  History,
} from "lucide-react";
import {
  formatDisplayDate,
  formatEvaluationAmount,
  formatRemainingDays,
} from "../../model";
import {
  RevisionComparison,
} from "../../RequestDetail";
import type { ApprovalRowContext } from "./approval-row-model";
import { ApprovalOfferDecision } from "./ApprovalOfferDecision";

export function ApprovalDecisionWorkspace({ context }: { context: ApprovalRowContext ;}) {
  const {
    request,
    user,
    revisionsByRequest,
    isActionable,
    evaluation,
    selectedOffer,
    dossierComplete,
    gmApproval,
    managerApproval,
    isCompleted,
    finalSelectionChanged,
    displayedOffer,
    gmFinalOffer,
    gmChangedManagerSelection,
  } = context;
  return (<>
    <div className="manager-decision-workspace">
      <main className="manager-decision-primary">
        <header className="approval-title">
          <div>
            <span>{request.id}</span>
            <span
              className={`approval-priority priority-${request.priority.toLocaleLowerCase("tr")}`}
            >
              {request.priority} öncelik
            </span>
          </div>
          <h2>{request.title}</h2>
          <p>{request.meta}</p>
        </header>
        <RevisionComparison
          revisions={
            revisionsByRequest[request.id] ?? []
          }
        />
        {isCompleted ? (
          <section className="completed-approval-record">
            <header>
              <div>
                <span>Tamamlanan satın alma</span>
                <strong>
                  Sonuç ve denetim kaydı
                </strong>
              </div>
              <span>Salt okunur</span>
            </header>
            {finalSelectionChanged ? (
              <div className="completed-selection-change">
                <strong>
                  Nihai seçim Genel Müdür tarafından
                  değiştirildi
                </strong>
                <div>
                  <span>
                    <small>Önceki öneri</small>
                    <b>
                      {managerApproval?.decidedOfferSupplier ??
                        selectedOffer?.supplier ??
                        "Eski kayıtta bulunmuyor"}
                    </b>
                  </span>
                  <span>
                    <small>Nihai teklif</small>
                    <b>
                      {gmApproval?.decidedOfferSupplier ??
                        displayedOffer?.supplier ??
                        "Eski kayıtta bulunmuyor"}
                    </b>
                  </span>
                  <span>
                    <small>Değiştiren</small>
                    <b>
                      {gmApproval?.decidedBy ??
                        "Genel Müdür"}
                    </b>
                  </span>
                  <span>
                    <small>Karar tarihi</small>
                    <b>
                      {gmApproval?.decidedAt
                        ? new Date(
                          gmApproval.decidedAt,
                        ).toLocaleString("tr-TR")
                        : "Eski kayıtta bulunmuyor"}
                    </b>
                  </span>
                </div>
                <p>
                  <strong>Gerekçe:</strong>{" "}
                  {gmApproval?.decisionNote ??
                    "Eski kayıtta gerekçe bulunmuyor."}
                </p>
              </div>
            ) : null}
            {!evaluation?.recommendation ||
              !managerApproval?.decidedOfferId ||
              !gmApproval?.decidedOfferId ? (
              <p className="completed-record-legacy">
                Bu eski kaydın bazı karar bağlantıları
                bulunmuyor. Eksik alanlar için
                varsayım üretilmeden salt okunur
                gösteriliyor.
              </p>
            ) : null}
            <div className="completed-decision-trail">
              <article>
                <span>1</span>
                <div>
                  <small>
                    Satın Alma Uzmanı önerisi
                  </small>
                  <strong>
                    {selectedOffer?.supplier ??
                      "Teklif bağlantısı bulunmuyor"}
                  </strong>
                  <p>
                    {evaluation?.recommendation
                      ?.createdByName ??
                      "Kayıtlı uzman yok"}
                    {evaluation?.recommendation
                      ?.createdAt
                      ? ` · ${new Date(
                        evaluation.recommendation
                          .createdAt,
                      ).toLocaleString("tr-TR")}`
                      : ""}
                  </p>
                  {evaluation?.recommendation
                    ?.rationale ? (
                    <p>
                      {
                        evaluation.recommendation
                          .rationale
                      }
                    </p>
                  ) : null}
                </div>
              </article>
              <article>
                <span>2</span>
                <div>
                  <small>
                    Satın Alma Müdürü kararı
                  </small>
                  <strong>
                    {managerApproval?.decidedOfferSupplier ??
                      "Teklif bağlantısı bulunmuyor"}
                  </strong>
                  <p>
                    {managerApproval?.decidedBy ??
                      "Kayıtlı karar sahibi yok"}
                    {managerApproval?.decidedAt
                      ? ` · ${new Date(
                        managerApproval.decidedAt,
                      ).toLocaleString("tr-TR")}`
                      : ""}
                  </p>
                  {managerApproval?.decisionNote ? (
                    <p>
                      {managerApproval.decisionNote}
                    </p>
                  ) : null}
                </div>
              </article>
              <article>
                <span>3</span>
                <div>
                  <small>
                    Genel Müdür nihai kararı
                  </small>
                  <strong>
                    {gmApproval?.decidedOfferSupplier ??
                      displayedOffer?.supplier ??
                      "Teklif bağlantısı bulunmuyor"}
                  </strong>
                  <p>
                    {gmApproval?.decidedBy ??
                      "Kayıtlı karar sahibi yok"}
                    {gmApproval?.decidedAt
                      ? ` · ${new Date(
                        gmApproval.decidedAt,
                      ).toLocaleString("tr-TR")}`
                      : ""}
                  </p>
                  {gmApproval?.decisionNote ? (
                    <p>{gmApproval.decisionNote}</p>
                  ) : null}
                </div>
              </article>
            </div>
          </section>
        ) : null}
        <ApprovalOfferDecision context={context} />
        {user.role === "GENEL_MUDUR" &&
          isActionable &&
          dossierComplete &&
          gmFinalOffer ? (
          <section
            className="gm-final-decision"
            aria-label="Nihai tedarikçi kararı"
          >
            <div className="gm-final-decision-heading">
              <div className="gm-final-decision-choice">
                <span
                  className="gm-final-decision-check"
                  aria-hidden="true"
                >
                  &#10003;
                </span>
                <div>
                  <span>Nihai karar</span>
                  <strong>
                    {gmFinalOffer.supplier}
                  </strong>
                  <small>
                    {gmFinalOffer.offerNo}
                  </small>
                </div>
              </div>
              <span
                className={`gm-final-decision-status${gmChangedManagerSelection
                    ? " is-changed"
                    : ""
                  }`}
              >
                {gmChangedManagerSelection
                  ? "Farklı seçim"
                  : "Öneriyle uyumlu"}
              </span>
            </div>
            <p className="gm-final-decision-copy">
              {gmChangedManagerSelection
                ? "Müdür önerisinden farklı seçimi gerekçelendirmek için alttaki nihai onay düğmesini kullanın."
                : "Satın Alma Müdürünün önerdiği teklif nihai karar olarak onaylanacak."}
            </p>
          </section>
        ) : null}
      </main>
      <aside
        className="manager-decision-aside"
        aria-label="Talep bağlamı ve kanıtlar"
      >
        <section className="manager-request-summary">
          <span>Talep özeti</span>
          <dl>
            <div>
              <dt>Miktar</dt>
              <dd>
                {request.quantity?.toLocaleString(
                  "tr-TR",
                ) ?? "—"}{" "}
                {request.unit ?? "birim"}
              </dd>
            </div>
            <div>
              <dt>Talep eden</dt>
              <dd>{request.requester}</dd>
              <small>{request.department}</small>
            </div>
            <div>
              <dt>İhtiyaç tarihi</dt>
              <dd>
                {formatDisplayDate(request.due)}
              </dd>
              <small className="manager-request-deadline">
                {formatRemainingDays(request.due)}
              </small>
            </div>
            <div>
              <dt>Öncelik</dt>
              <dd>{request.priority}</dd>
              {request.priority !== "Normal" ? (
                <small className="manager-request-priority-note">
                  {request.priority === "Kritik"
                    ? "Öncelikli karar gerekli"
                    : "Yakın takip gerekli"}
                </small>
              ) : null}
            </div>
          </dl>
        </section>
        {evaluation && displayedOffer ? (
          <section className="approval-purchase-history manager-price-history">
            <header>
              <History size={18} strokeWidth={1.8} />
              <strong>Fiyat geçmişi</strong>
            </header>
            {evaluation.historicalPurchases?.filter(
              (purchase) =>
                purchase.supplierId ===
                displayedOffer.supplierId,
            ).length ? (
              <div className="approval-purchase-history-list">
                {evaluation.historicalPurchases
                  .filter(
                    (purchase) =>
                      purchase.supplierId ===
                      displayedOffer.supplierId,
                  )
                  .map((purchase) => {
                    const currentUnitPrice =
                      displayedOffer.unitPrice;
                    const difference =
                      currentUnitPrice === null
                        ? null
                        : currentUnitPrice -
                        purchase.unitPrice;
                    const percentage =
                      difference === null ||
                        purchase.unitPrice === 0
                        ? null
                        : (difference /
                          purchase.unitPrice) *
                        100;
                    return (
                      <article key={purchase.orderNo}>
                        <div>
                          <small>
                            {new Date(
                              purchase.orderedAt,
                            ).toLocaleDateString(
                              "tr-TR",
                            )}{" "}
                            · {purchase.orderNo}
                          </small>
                          <strong>
                            {purchase.materialName}
                          </strong>
                          <span>
                            {purchase.quantity.toLocaleString(
                              "tr-TR",
                            )}{" "}
                            {purchase.unit} ·{" "}
                            {purchase.status}
                          </span>
                        </div>
                        <div>
                          <small>
                            Önceki birim fiyat
                          </small>
                          <strong>
                            {formatEvaluationAmount(
                              purchase.unitPrice,
                              purchase.currency,
                            )}{" "}
                            / {purchase.unit}
                          </strong>
                          <span>
                            Toplam{" "}
                            {formatEvaluationAmount(
                              purchase.totalAmount,
                              purchase.currency,
                            )}
                          </span>
                        </div>
                        <div>
                          <small>Güncel fark</small>
                          <strong>
                            {difference === null
                              ? "Karşılaştırma yok"
                              : `${difference >= 0 ? "+" : ""}${formatEvaluationAmount(difference, purchase.currency)}`}
                          </strong>
                          <span>
                            {percentage === null
                              ? "Oran hesaplanamadı"
                              : `%${percentage >= 0 ? "+" : ""}${percentage.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`}
                          </span>
                        </div>
                      </article>
                    );
                  })}
              </div>
            ) : (
              <div className="manager-evidence-empty">
                <strong>
                  Karşılaştırılabilir kayıt yok
                </strong>
                <p>
                  Fiyat eğilimi doğrulanamıyor; kararı
                  güncel teklif koşullarıyla verin.
                </p>
              </div>
            )}
          </section>
        ) : null}
      </aside>
    </div>
  </>);
}
